import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { Home, Mail, ArrowRight, Check, Sparkles, Shield, Zap } from 'lucide-react'
import { data, redirect, Form, useSearchParams, useLoaderData, Link } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { motion } from 'framer-motion'
import {
	EmailTemplate,
	EmailHeading,
	EmailText,
	EmailButton,
	EmailCodeBox,
	EmailDivider,
} from '#app/components/email-template.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import {
	ProviderConnectionForm,
	providerNames,
} from '#app/utils/connections.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { type Route } from './+types/signup.ts'
import { prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const SignupSchema = z.object({
	email: EmailSchema,
})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)

	// Get questionnaire data from session
	const cookieHeader = request.headers.get('cookie')
	const session = await authSessionStorage.getSession(cookieHeader)
	const questionnaireData = session.get('questionnaireData')
	const addressData = session.get('addressData')

	return {
		questionnaireData,
		addressData
	}
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()

	await checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'Er bestaat al een gebruiker met dit e-mailadres',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { email } = submission.value

	// Get onboarding data from session to pass through verification
	const authSession = await authSessionStorage.getSession(
		request.headers.get('cookie'),
	)
	const onboardingData = authSession.get('onboardingData')
	const questionnaireCompleted = authSession.get('questionnaireCompleted')

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
	})

	const response = await sendEmail({
		to: email,
		subject: `Welkom bij Woonplan!`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		return data(
			{ result: submission.reply({ formErrors: [response.error.message] }) },
			{ status: 500 },
		)
	}
}

function SignupEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<EmailTemplate>
			<EmailHeading>Welkom bij Woonplan!</EmailHeading>
			<EmailText>
				We zijn blij dat je de eerste stap zet naar een succesvolle renovatie.
			</EmailText>
			<EmailText>
				Klik op de onderstaande knop om je account te activeren en verder te gaan met je renovatieproject:
			</EmailText>
			<EmailButton href={onboardingUrl}>Account Activeren</EmailButton>
			<EmailDivider />
			<EmailText>
				Of gebruik deze code om je account te activeren:
			</EmailText>
			<EmailCodeBox code={otp} />
			<EmailText>Deze code is 10 minuten geldig.</EmailText>
		</EmailTemplate>
	)
}

const features = [
	{
		icon: Sparkles,
		title: "AI-gestuurde aanbevelingen",
		description: "Krijg gepersonaliseerde renovatietips"
	},
	{
		icon: Shield,
		title: "Veilig & Betrouwbaar",
		description: "Jouw gegevens zijn bij ons veilig"
	},
	{
		icon: Zap,
		title: "Direct aan de slag",
		description: "Begin vandaag met je renovatie"
	}
]

export default function SignupRoute() {
	const { questionnaireData, addressData } = useLoaderData<typeof loader>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		defaultValue: { email: '' },
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: SignupSchema })
		},
	})

	// Calculate questionnaire progress
	const totalQuestions = 13
	const answeredQuestions = questionnaireData ? Object.keys(questionnaireData).length : 0
	const progress = Math.round((answeredQuestions / totalQuestions) * 100)

	// Get street view URL if available
	const streetViewUrl = addressData?.streetViewUrl

	return (
		<div className="min-h-screen flex">
			{/* Left Panel - Beautiful Background */}
			<div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
				{/* Background with gradient overlay */}
				{streetViewUrl ? (
					<div
						className="absolute inset-0 bg-cover bg-center"
						style={{ backgroundImage: `url(${streetViewUrl})` }}
					/>
				) : (
					<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800" />
				)}

				<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-transparent" />

				{/* Content */}
				<div className="relative z-10 flex flex-col justify-between p-12 text-white">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-white/10 backdrop-blur-sm rounded-xl">
							<Home className="h-8 w-8 text-white" />
						</div>
						<div className="grid leading-tight">
							<span className="text-2xl font-light">woon</span>
							<span className="text-2xl font-bold">plan</span>
						</div>
					</div>

					<div className="space-y-8">
						{addressData && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								className="space-y-3"
							>
								<h2 className="text-4xl font-bold">
									{addressData.street} {addressData.houseNumber}
								</h2>
								<p className="text-xl text-white/90">
									{addressData.postalCode} {addressData.city}
								</p>
							</motion.div>
						)}

						{questionnaireData && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.1 }}
								className="space-y-4"
							>
								<div className="flex items-center justify-between">
									<span className="text-white/80">Vragenlijst voortgang</span>
									<span className="font-semibold">{progress}%</span>
								</div>
								<div className="h-2 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
									<motion.div
										className="h-full bg-gradient-to-r from-green-400 to-emerald-400 rounded-full"
										initial={{ width: 0 }}
										animate={{ width: `${progress}%` }}
										transition={{ duration: 1, ease: "easeOut" }}
									/>
								</div>
								<p className="text-sm text-white/70">
									{answeredQuestions} van {totalQuestions} vragen beantwoord
								</p>
							</motion.div>
						)}

						<div className="pt-6 border-t border-white/20">
							<blockquote className="space-y-2">
								<p className="text-lg italic text-white/90">
									"Woonplan heeft ons geholpen om onze droomrenovatie te realiseren.
									De AI-aanbevelingen waren spot-on!"
								</p>
								<footer className="text-sm text-white/70">
									— Sarah de Vries, Amsterdam
								</footer>
							</blockquote>
						</div>
					</div>
				</div>
			</div>

			{/* Right Panel - Signup Form */}
			<div className="flex-1 flex items-center justify-center px-8 bg-gray-50/50">
				<motion.div
					initial={{ opacity: 0, x: 20 }}
					animate={{ opacity: 1, x: 0 }}
					transition={{ duration: 0.5 }}
					className="w-full max-w-md space-y-8"
				>
					{/* Mobile Logo */}
					<div className="flex lg:hidden items-center gap-3 justify-center">
						<div className="p-2 bg-blue-600/10 rounded-xl">
							<Home className="h-8 w-8 text-blue-600" />
						</div>
						<div className="grid leading-tight">
							<span className="text-2xl font-light text-gray-700">woon</span>
							<span className="text-2xl font-bold text-gray-900">plan</span>
						</div>
					</div>

					<div className="space-y-2 text-center">
						<h1 className="text-3xl font-bold tracking-tight">
							Maak je account aan
						</h1>
						<p className="text-gray-600">
							Laatste stap om je renovatiereis te beginnen
						</p>
					</div>

					{/* Features for mobile */}
					<div className="grid grid-cols-1 gap-3 lg:hidden">
						{features.map((feature, i) => (
							<motion.div
								key={feature.title}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: i * 0.1 }}
								className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100"
							>
								<div className="p-2 bg-blue-50 rounded-lg">
									<feature.icon className="h-4 w-4 text-blue-600" />
								</div>
								<div className="flex-1">
									<h3 className="text-sm font-medium text-gray-900">{feature.title}</h3>
									<p className="text-xs text-gray-600 mt-0.5">{feature.description}</p>
								</div>
							</motion.div>
						))}
					</div>

					<Form
						method="POST"
						{...getFormProps(form)}
						className="space-y-6"
					>
						<HoneypotInputs />
						<div className="space-y-2">
							<Label htmlFor={fields.email.id}>
								E-mailadres
							</Label>
							<div className="relative">
								<Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
								<Input
									{...getInputProps(fields.email, { type: 'email' })}
									autoFocus
									className="pl-10 h-11"
									autoComplete="email"
									placeholder="jouw@email.nl"
								/>
							</div>
							<ErrorList errors={fields.email.errors} id={fields.email.errorId} />
						</div>

						<ErrorList errors={form.errors} id={form.errorId} />

						<Button
							className="w-full h-11 text-base font-medium"
							type="submit"
							disabled={isPending}
						>
							{isPending ? (
								<>
									<span className="mr-2">Account aanmaken...</span>
									<motion.span
										animate={{ rotate: 360 }}
										transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
									>
										⏳
									</motion.span>
								</>
							) : (
								<>
									Account aanmaken
									<ArrowRight className="ml-2 h-4 w-4" />
								</>
							)}
						</Button>
					</Form>

					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<div className="w-full border-t border-gray-200" />
						</div>
						<div className="relative flex justify-center text-sm">
							<span className="px-4 bg-gray-50 text-gray-500">Of meld je aan met</span>
						</div>
					</div>

					<div className="grid grid-cols-2 gap-3">
						{providerNames.map((providerName) => (
							<ProviderConnectionForm
								key={providerName}
								type="Signup"
								providerName={providerName}
								redirectTo={redirectTo}
							/>
						))}
					</div>

					<p className="text-center text-sm text-gray-600">
						Heb je al een account?{' '}
						<Link
							to="/login"
							className="font-medium text-blue-600 hover:text-blue-500 hover:underline"
						>
							Log hier in
						</Link>
					</p>

					<p className="text-center text-xs text-gray-500">
						Door je aan te melden, ga je akkoord met onze{' '}
						<Link to="/terms" className="underline hover:text-gray-700">
							Algemene Voorwaarden
						</Link>{' '}
						en{' '}
						<Link to="/privacy" className="underline hover:text-gray-700">
							Privacybeleid
						</Link>
					</p>
				</motion.div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}